import { useMemo, useState } from 'react';
import { IonSpinner } from '@ionic/react';
import { useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Modal } from '../ui/Modal';
import { VerificationCode } from './VerificationCode';
import { useDenyHandoff, useMyOrders, useVerifyOrder } from '../../hooks/useOrders';
import { useAuthStore } from '../../store/useAuthStore';
import type { MyOrderSummary } from '../../types/order';

/**
 * Terminal recovery routes, exempt for the same reason every other app gate
 * exempts them (see App.tsx): they are the user's only way back to a working
 * app, and the one action this block demands — confirming — needs a reachable
 * backend. Blocking on top of them would strand a user with no way out.
 */
const GATE_EXEMPT_PATHS = new Set<string>([
  '/no-network',
  '/service-unavailable',
  '/update-required',
]);

/**
 * Returns the order, if any, that the eater is being held on.
 *
 * The gate fires on exactly one state: `ready_to_pickup` + the CHEF has
 * confirmed handoff + the eater has not. That is the only window where the food
 * has demonstrably changed hands while the order is still in flight — before the
 * chef's confirm the eater may not even have arrived yet, and blocking them then
 * would be blocking them for the chef's slowness.
 *
 * A row without a verification code is skipped rather than blocked on: confirming
 * is the only way out of this modal, so gating without one would brick the app
 * for that user. The code is ungated at `ready_to_pickup` on every surface, so
 * in practice this only guards against a partial payload.
 *
 * A row without a listing is skipped for the same reason: /orders/mine omits
 * `listing` when it has been deleted, and the BE verify then rejects with
 * "listing missing on order", so confirming could never clear the modal. That
 * happens when a listing delete's best-effort cascade-cancel misses an order.
 *
 * Oldest first, so a user sitting on two of these clears them in the order they
 * happened rather than in whatever order the page cursor returned.
 */
function pickBlockingOrder(orders: MyOrderSummary[]): MyOrderSummary | undefined {
  return orders
    .filter(
      (o) =>
        o.status === 'ready_to_pickup' &&
        o.listerConfirmed &&
        !o.ordererConfirmed &&
        o.verificationCode != null &&
        o.listing != null,
    )
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0];
}

/**
 * App-wide block: once the chef has attested handoff, the eater cannot use the
 * rest of the app until they confirm their half. Deliberately harder than the
 * force-sticky confirm-reminder toast that fires alongside it (wsService
 * `order_confirm_reminder`) — that toast is dismissable with an X, so an eater
 * who wants to walk away from the handshake can, and the order then sits open
 * until the chef declares a no-show after the window closes.
 *
 * No dismiss affordance on purpose: `onClose` is a no-op (the shared Modal
 * closes on backdrop click) and the X is hidden. The exits are confirming,
 * answering "No, I did not get my dish" (which undoes the chef's Handed Off and
 * tells them, so the order goes back to waiting on a handoff), or the order
 * leaving this state (chef cancels / no-show), which the query invalidation
 * below picks up live. The deny path has no repeat limit: if the chef presses
 * Handed Off again, the gate comes back.
 *
 * Reads the buyer-history list rather than a single order so the block survives
 * a cold start: the eater who pocketed their food and closed the app gets it on
 * next open, without having to navigate to the order first. Shares its query key
 * with ActiveOrdersPanel on the home feed, so this costs no extra request there.
 *
 * Polls those rows while a blocking order is up. The socket is the primary
 * signal that the order left this state, but a backgrounded phone can miss it,
 * and a stale gate has no exit: every confirm 403s against the dead order.
 */
const OrderConfirmGateInner: React.FC = () => {
  const { t } = useTranslation('orders');
  const { data } = useMyOrders(false, { pollWhile: (orders) => !!pickBlockingOrder(orders) });
  const verifyMutation = useVerifyOrder();
  const denyMutation = useDenyHandoff();
  const [verifyError, setVerifyError] = useState('');
  const busy = verifyMutation.isPending || denyMutation.isPending;

  const order = useMemo(() => pickBlockingOrder(data?.pages.flat() ?? []), [data]);

  if (!order) return null;

  const code = order.verificationCode!;
  const name = order.listing?.listerSummary?.businessName ?? order.listing?.listerSummary?.displayName ?? '';

  const handleConfirm = () => {
    if (busy) return;
    setVerifyError('');
    verifyMutation.mutate(
      { id: order.orderId, code },
      {
        onSuccess: (result) => {
          if (!result.success) setVerifyError(t('verify.wrongCode'));
        },
      },
    );
  };

  const handleDeny = () => {
    if (busy) return;
    setVerifyError('');
    denyMutation.mutate(
      { id: order.orderId },
      {
        onError: () => setVerifyError(t('confirmGate.denyError')),
      },
    );
  };

  return (
    <Modal
      isOpen
      // Intentionally inert: this modal has no dismiss path. See the component
      // doc — confirming (or the order leaving this state) is the only exit.
      onClose={() => {}}
      hideCloseButton
      size="sm"
      className="space-y-4"
      zIndexClass="z-[70]"
    >
      <h2 className="text-center text-lg font-bold text-foreground">
        {t('confirmGate.title')}
      </h2>

      {order.listing?.title && (
        <p className="text-center text-sm font-semibold text-brand-primary">
          {order.listing.title}
        </p>
      )}

      <p className="whitespace-pre-line text-center text-sm text-muted-foreground">
        {name ? (
          <Trans
            i18nKey="orders:confirmGate.body"
            values={{ name }}
            components={{ name: <span className="font-semibold text-brand-primary" /> }}
          />
        ) : (
          t('confirmGate.bodyNoName')
        )}
      </p>

      <VerificationCode code={code} />

      <p className="text-center text-xs text-muted-foreground">
        {t('confirmGate.footnote')}
      </p>

      {verifyError && (
        <p className="text-center text-sm text-brand-danger">{verifyError}</p>
      )}

      {/* Yes/No pair: same rectangle, filled for confirm, outlined danger for deny. */}
      <button
        type="button"
        onClick={handleConfirm}
        disabled={busy}
        className="flex w-full items-center justify-center rounded-lg bg-brand-primary py-2 text-sm font-semibold text-white active:opacity-80 disabled:opacity-50"
      >
        {verifyMutation.isPending ? <IonSpinner name="crescent" className="h-4 w-4" /> : t('confirmGate.confirmCta')}
      </button>

      <button
        type="button"
        onClick={handleDeny}
        disabled={busy}
        className="flex w-full items-center justify-center rounded-lg btn-outline border-brand-danger py-2 text-sm font-semibold text-brand-danger active:opacity-80 disabled:opacity-50"
      >
        {denyMutation.isPending ? <IonSpinner name="crescent" className="h-4 w-4" /> : t('confirmGate.denyCta')}
      </button>
    </Modal>
  );
};

/**
 * Auth split so the buyer-history query never runs for a logged-out visitor —
 * the inner component owns the hook, and mounting it at all is the auth check.
 */
export const OrderConfirmGate: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { pathname } = useLocation();
  if (!isAuthenticated || GATE_EXEMPT_PATHS.has(pathname)) return null;
  return <OrderConfirmGateInner />;
};
